// Observed Okendo Widget Plus seam, PR86 Trifecta, 2026-10-12.
// Public DOM implementation details, not a documented provider insertion API.
const seam = Object.freeze({
  widget: '[data-oke-widget]',
  container: '.js-oke-widgetSize',
  header: '.oke-w-header',
  controls: '.oke-w-controls',
  panels: '.oke-w-main',
});
const normalize = (text) => (text || '').replace(/\s+/g, ' ').trim();
const single = (items) => items.length === 1 ? items[0] : null;
const direct = (element, selector) => [...element.children].filter((child) => child.matches(selector));

const moduleRoots = Object.freeze({
  'rating-average': '.oke-w-ratingAverageModule',
  'media-carousel': '.oke-w-mediaCarouselModule',
  'reviews-keywords': '.oke-w-reviewsKeywordsModule',
  'reviews-summary': '.oke-w-reviewsSummaryModule',
});
const nativeSummary = '.oke-w-reviewsSummaryModule,.oke-reviewsSummary';
const cleanBoundary = (element) => [...element.childNodes].every((node) =>
  node.nodeType === 1 || node.nodeType === 8 || (node.nodeType === 3 && !normalize(node.textContent)));

function publicHeader(document) {
  try {
    const settings = JSON.parse(document.getElementById('oke-reviews-settings')?.textContent || 'null');
    const blocks = settings?.widgetSettings?.reviewsWidget?.header?.blocks;
    if (!Array.isArray(blocks) || !blocks.length || !blocks.every((block) =>
      Array.isArray(block.modules) && block.modules.every((module) =>
        Object.hasOwn(moduleRoots, module.name)))) return null;
    const topics = single(blocks.flatMap((block) => block.modules).filter((module) => module.name === 'reviews-keywords'));
    if (!normalize(topics?.heading)) return null;
    return { blocks, topics: normalize(topics.heading) };
  } catch {
    return null;
  }
}

function knownHeader(header, config) {
  if (!cleanBoundary(header) || header.children.length !== 1) return false;
  const content = header.children[0];
  if (!content.matches('.oke-w-header-content') || !cleanBoundary(content) ||
      content.children.length !== config.blocks.length) return false;
  let topicRoot;
  for (const [index, block] of [...content.children].entries()) {
    const modules = config.blocks[index].modules;
    if (!block.matches('.oke-w-header-content-block') || !cleanBoundary(block) ||
        block.children.length !== modules.length) return false;
    for (const [moduleIndex, wrapper] of [...block.children].entries()) {
      const module = modules[moduleIndex];
      if (!wrapper.matches('.oke-w-header-content-module') || !cleanBoundary(wrapper) ||
          wrapper.children.length > 1) return false;
      const root = wrapper.children[0];
      if (root && !root.matches(moduleRoots[module.name])) return false;
      if (module.name === 'reviews-keywords') topicRoot = root;
    }
  }
  // Native component interiors stay opaque and untouched. Runtime compatibility
  // remains required; the strict guard rejects unknown content at module seams.
  const headings = topicRoot && [...topicRoot.querySelectorAll('h1,h2,h3,h4,h5,h6,[role="heading"]')];
  return headings?.length === 1 && normalize(headings[0].textContent) === config.topics;
}

function slot(section, productId, document, owned) {
  const widget = single([...section.querySelectorAll(seam.widget)]);
  if (!widget || widget.getAttribute('data-oke-reviews-product-id') !== productId ||
      !widget.hasAttribute('data-oke-rendered')) return { state: 'waiting-or-ambiguous-widget' };
  const container = single([...widget.querySelectorAll(seam.container)]);
  if (!container) return { state: 'unknown-container' };
  const header = single(direct(container, seam.header));
  const controls = single(direct(container, seam.controls));
  const panels = direct(container, seam.panels);
  const children = [...container.children];
  const tablist = controls && single([...controls.querySelectorAll('[role="tablist"]')]);
  if (!header || !controls || !tablist || !panels.length ||
      children.indexOf(header) >= children.indexOf(controls) ||
      panels.some((panel) => children.indexOf(panel) <= children.indexOf(controls))) {
    return { state: 'unknown-slot' };
  }
  // Direct children must be the observed provider shape plus this owned node.
  if (children.some((child) => child !== header && child !== controls && !panels.includes(child) &&
      child !== owned)) return { state: 'unknown-slot' };
  // Markers come from exact public provider render chunks, not heading text.
  // Source-derived presence is not an actual-preview positive-state proof.
  if (section.querySelector(nativeSummary)) return { state: 'native-summary-present' };
  const config = publicHeader(document);
  if (!config || !knownHeader(header, config)) return { state: 'unknown-native-summary' };
  return { state: 'observed-known-header-no-native-marker', container, controls };
}

class ProductReviewsSummary extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback();
    this.section = this.closest('.product-reviews');
    this.template = this.querySelector('template');
    const id = this.dataset.productId;
    if (!this.section || this.section.querySelectorAll('product-reviews-summary').length !== 1 ||
        !this.template?.content.firstElementChild || !/^\d+$/.test(id || '')) return;
    this.productId = 'shopify-' + id;
    this.sourceId = id;
    this.repairs = 0;
    this.mounted = false;
    this.active = true;
    this.owned = this.template.content.firstElementChild.cloneNode(true);
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(this.section, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-oke-rendered', 'data-oke-reviews-product-id', 'data-product-id', 'class'],
    });
    this.schedule();
  }

  schedule() {
    if (!this.active || this.frame != null) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      if (this.active && this.isConnected) this.reconcile();
    });
  }

  reconcile() {
    if (this.dataset.productId !== this.sourceId || this.closest('.product-reviews') !== this.section ||
        this.section.querySelectorAll('product-reviews-summary').length !== 1) {
      this.stop('product-changed');
      return;
    }
    const result = slot(this.section, this.productId, this.ownerDocument, this.owned);
    this.dataset.summaryState = result.state;
    if (!result.container) {
      this.owned.remove();
      return;
    }
    if (this.owned.parentElement === result.container && this.owned.nextElementSibling === result.controls) return;
    // Initial mount plus at most two repairs. Persistent provider erasure stops,
    // including repeated full-container replacements. No polling/reinsertion loop.
    if (this.mounted && ++this.repairs > 2) {
      this.stop('provider-churn');
      return;
    }
    result.container.insertBefore(this.owned, result.controls);
    this.mounted = true;
  }

  stop(state) {
    this.disconnectedCallback();
    this.dataset.summaryState = state;
  }

  disconnectedCallback() {
    this.active = false;
    this.observer?.disconnect();
    this.observer = null;
    if (this.frame != null) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.owned?.remove();
    this.owned = null;
  }
}

if (!customElements.get('product-reviews-summary')) {
  customElements.define('product-reviews-summary', ProductReviewsSummary);
}
